import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, BRL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LayoutDashboard, Receipt, TrendingUp, Users, LogOut, Wallet, PiggyBank, PanelLeft, Menu, CreditCard, FileText, UserSquare, Bell, AlertTriangle, CalendarClock } from "lucide-react";

const nav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/despesas", label: "Despesas", icon: Receipt },
  { to: "/pagamentos", label: "Pagamentos", icon: CreditCard },
  { to: "/contratos", label: "Contratos", icon: FileText },
  { to: "/clientes", label: "Clientes", icon: UserSquare },
  { to: "/caixa", label: "Caixa", icon: PiggyBank },
  { to: "/previsoes", label: "Previsões", icon: TrendingUp },
];

export default function Layout({ children }) {
  const { user, logout, appName } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [alerts, setAlerts] = useState({ upcoming: [], overdue: [], count: 0 });
  const [alertOpen, setAlertOpen] = useState(false);

  useEffect(() => {
    api.get("/alerts/upcoming?days=3").then((r) => {
      setAlerts(r.data);
      if (r.data.count > 0 && !sessionStorage.getItem("alertShown")) {
        setAlertOpen(true);
        sessionStorage.setItem("alertShown", "1");
      }
    }).catch(() => {});
  }, []);

  const items = [...nav];
  if (user?.role === "admin") items.push({ to: "/admin", label: "Admin", icon: Users });

  const doLogout = () => { logout(); navigate("/login"); };

  const NavList = ({ onNavigate }) => (
    <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
      {items.map((it) => (
        <NavLink key={it.to} to={it.to} end={it.end} onClick={onNavigate} data-testid={`nav-${it.label.toLowerCase()}`}
          className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"}`}>
          <it.icon className="h-4 w-4 shrink-0" aria-hidden />
          {it.label}
        </NavLink>
      ))}
    </nav>
  );

  const Brand = () => (
    <div className="h-16 flex items-center gap-2 px-6 border-b border-border/60 shrink-0">
      <div className="h-8 w-8 rounded-md bg-primary flex items-center justify-center shrink-0"><Wallet className="h-4 w-4 text-primary-foreground" aria-hidden /></div>
      <span className="font-head font-extrabold text-lg tracking-tight truncate">{appName}</span>
    </div>
  );

  const Footer = () => (
    <div className="p-3 border-t border-border/60 shrink-0">
      <div className="px-3 py-2 mb-2">
        <p className="text-sm font-semibold truncate">{user?.name}</p>
        <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
      </div>
      <Button variant="ghost" data-testid="logout-button" className="w-full justify-start gap-3 text-muted-foreground hover:text-destructive" onClick={doLogout}><LogOut className="h-4 w-4" /> Sair</Button>
    </div>
  );

  const AlertRow = ({ a, overdue }) => (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-border/60 last:border-0" data-testid={`alert-item-${a.id}`}>
      <div className="min-w-0">
        <p className="font-medium truncate">{a.name}</p>
        <p className="text-xs text-muted-foreground">{a.client_name ? a.client_name + " · " : ""}{new Date(a.date).toLocaleDateString("pt-BR")}</p>
      </div>
      <span className={`text-sm font-semibold tnum ${overdue ? "text-destructive" : "text-amber-600 dark:text-amber-500"}`}>{BRL(a.remaining)}</span>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-background">
      {!collapsed && (
        <aside className="w-64 shrink-0 border-r border-border/60 bg-card hidden md:flex flex-col" data-testid="desktop-sidebar">
          <Brand /><NavList /><Footer />
        </aside>
      )}

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-72 p-0 flex flex-col" data-testid="mobile-sidebar">
          <SheetTitle className="sr-only">Menu de navegação</SheetTitle>
          <Brand /><NavList onNavigate={() => setMobileOpen(false)} /><Footer />
        </SheetContent>
      </Sheet>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-border/60 bg-card/80 backdrop-blur-xl flex items-center justify-between px-4 md:px-8 sticky top-0 z-20">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="md:hidden" data-testid="mobile-menu-button" onClick={() => setMobileOpen(true)}><Menu className="h-5 w-5" /></Button>
            <Button variant="ghost" size="icon" className="hidden md:inline-flex" data-testid="sidebar-toggle-button" onClick={() => setCollapsed((c) => !c)} title={collapsed ? "Mostrar menu" : "Ocultar menu"}><PanelLeft className="h-5 w-5" /></Button>
            <div className="md:hidden flex items-center gap-2"><Wallet className="h-5 w-5 text-primary" aria-hidden /><span className="font-head font-extrabold truncate max-w-[120px]">{appName}</span></div>
          </div>
          <div className="flex items-center gap-3">
            <button className="relative p-2 rounded-md hover:bg-accent transition-colors" data-testid="alerts-bell" onClick={() => setAlertOpen(true)} title="Vencimentos">
              <Bell className="h-5 w-5 text-muted-foreground" />
              {alerts.count > 0 && <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-destructive text-white text-[10px] font-bold flex items-center justify-center" data-testid="alerts-count">{alerts.count}</span>}
            </button>
            <span className="text-xs px-2.5 py-1 rounded-full bg-secondary text-secondary-foreground font-medium capitalize">{user?.role === "admin" ? "Administrador" : "Usuário"}</span>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-8 max-w-7xl w-full mx-auto">{children}</main>
      </div>

      <Dialog open={alertOpen} onOpenChange={setAlertOpen}>
        <DialogContent className="sm:max-w-md" data-testid="alerts-modal">
          <DialogHeader><DialogTitle className="font-head text-2xl tracking-tight flex items-center gap-2"><Bell className="h-5 w-5 text-primary" /> Contas a pagar</DialogTitle></DialogHeader>
          {alerts.count === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Tudo em dia! Nenhuma conta vencendo nos próximos 3 dias. 🎉</p>
          ) : (
            <div className="space-y-4 max-h-[60vh] overflow-auto">
              {alerts.overdue.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-destructive flex items-center gap-1.5 mb-1"><AlertTriangle className="h-4 w-4" /> Vencidas ({alerts.overdue.length})</p>
                  {alerts.overdue.map((a) => <AlertRow key={a.id} a={a} overdue />)}
                </div>
              )}
              {alerts.upcoming.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-amber-600 dark:text-amber-500 flex items-center gap-1.5 mb-1"><CalendarClock className="h-4 w-4" /> Vencem em até 3 dias ({alerts.upcoming.length})</p>
                  {alerts.upcoming.map((a) => <AlertRow key={a.id} a={a} />)}
                </div>
              )}
              <Button className="w-full font-semibold" data-testid="alerts-goto-payments" onClick={() => { setAlertOpen(false); navigate("/pagamentos"); }}>Ver pagamentos</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
